/** @format */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import {
    HeadingColor,
    HeadingSize,
    HighlightColor,
} from '@mpb-com/feather/components/atoms/heading/types';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import AwardIcon from '@toucan/client/global/icons/award-winning.svg';
import CircularityIcon from '@toucan/client/global/icons/circularity.svg';
import HeartIcon from '@toucan/client/global/icons/heart.svg';
import OpenInNewTabIcon from '@toucan/client/global/icons/open-in-new-tab.svg';

import personsUsingCamerasImage from '@toucan/client/global/images/trust-block/2-persons-using-cameras.png';

import { useHelpCentreUrl } from '@toucan/client/global/intercom';
import { MarketContext } from '@toucan/client/global/market';
import { TRUSTPILOT_TEMPLATE_IDS } from '@toucan/client/global/trustpilot';

import {
    Link,
    LinkColor,
    LinkTarget,
} from '@toucan/client/shared/components/link';
import {
    Trustpilot,
    TrustpilotTheme,
} from '@toucan/client/shared/components/trustpilot';
import { getStaticImageUrl } from '@toucan/client/shared/helpers/image';

import { styles } from './product-trust-block.styles';
import { ProductTrustBlockProps } from './types';

export const DEFAULT_TEST_ID = 'product-trust-block';

export const StyledTrustBlock = styled('section')`
    ${styles}
`;

export const ProductTrustBlock: FunctionComponent<ProductTrustBlockProps> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
}: ProductTrustBlockProps) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const intercomUrl = useHelpCentreUrl(activeMarket);
    const impactSlug = `/${activeMarket.slug}/impact`;

    const backgroundImage = getStaticImageUrl(
        personsUsingCamerasImage?.medium?.png
    );

    const trustpilotTemplateId = TRUSTPILOT_TEMPLATE_IDS.slider;
    const highlight = <strong />;
    const title = (
        <Trans components={{ highlight }}>
            {t('Peace <highlight>of mind</highlight>')}
        </Trans>
    );

    const content = (
        <Trans components={{ highlight }}>
            {t(
                'Every year, we <highlight>recirculate</highlight> more than <highlight>500,000</highlight> used <highlight>cameras</highlight>, lenses and accessories. Everything comes with a <highlight>free</highlight> twelve-month <highlight>warranty</highlight>.'
            )}
        </Trans>
    );
    const trustpilotMention = t('We’re rated five stars on Trustpilot.');

    const uniqueSellingPoints = [
        {
            title: t('Plastic free shipping'),
            content: (
                <Trans components={{ highlight }}>
                    {t(
                        'All packaging is plastic free & our platform uses renewable electricity.'
                    )}
                </Trans>
            ),
            linkText: t('MPB and sustainability'),
            linkHref: impactSlug,
            icon: <CircularityIcon />,
        },
        {
            title: t('Trusted by 1,000,000'),
            content: (
                <Trans components={{ highlight }}>
                    {t(
                        'Over the past <highlight>ten</highlight> years, we’ve served more than <highlight>1,000,000</highlight> visual storytellers around the world.'
                    )}
                </Trans>
            ),

            icon: <HeartIcon />,
        },
        {
            title: t('First-class customer service'),
            content: (
                <Trans components={{ highlight }}>
                    {t(
                        'Our <highlight>award-winning</highlight> team in <highlight>Brighton</highlight> is here to help.'
                    )}
                </Trans>
            ),

            linkText: t('Help Centre'),
            linkHref: intercomUrl,
            icon: <AwardIcon />,
        },
    ];

    const uspBlocks = uniqueSellingPoints.map(
        ({ title, content, linkHref, linkText, icon }) => {
            return (
                <div className="product-trust-block__usp" key={title}>
                    <Icon
                        size={IconSize.XL}
                        variant={IconVariant.DECORATIVE_SECONDARY}
                        className="product-trust-block__icon"
                    >
                        {icon}
                    </Icon>
                    <Heading
                        size={HeadingSize.H4}
                        color={HeadingColor.TEXT_INVERSE}
                    >
                        {title}
                    </Heading>
                    <p>{content}</p>
                    {linkHref && linkText && (
                        <Link
                            href={linkHref}
                            linkColor={LinkColor.LIGHT}
                            target={LinkTarget.BLANK}
                            hasOutlineOnFocus
                        >
                            {linkText}
                            <Icon
                                size={IconSize.S}
                                className="product-trust-block__link__icon"
                                variant={IconVariant.CORE_SECONDARY}
                            >
                                <OpenInNewTabIcon />
                            </Icon>
                        </Link>
                    )}
                </div>
            );
        }
    );

    return (
        <StyledTrustBlock data-testid={dataTestId}>
            <div className="product-trust-block__wrapper">
                <div className="product-trust-block__container">
                    <div className="product-trust-block__content-container">
                        <Heading
                            size={HeadingSize.H2}
                            color={HeadingColor.TEXT_INVERSE}
                            highlightColor={HighlightColor.TEXT_SECONDARY}
                        >
                            {title}
                        </Heading>
                        <p
                            className="product-trust-block__content"
                            data-testid={`${DEFAULT_TEST_ID}__content`}
                        >
                            {content}
                        </p>
                        <p
                            className="product-trust-block__trustpilot-mention"
                            data-testid={`${DEFAULT_TEST_ID}__trustpilot-mention`}
                        >
                            {trustpilotMention}
                        </p>
                    </div>
                    <div className="product-trust-block__image">
                        <img
                            src={backgroundImage}
                            alt={t(
                                'Two photographers; on the left, a photographer is looking through the viewfinder, and on the right, the photographer is smiling and looking at the camera in their hands.'
                            )}
                        />
                    </div>
                    <div className="product-trust-block__trustpilot-widget">
                        <Trustpilot
                            width="100%"
                            height="240px"
                            templateId={trustpilotTemplateId}
                            className="trustpilot-widget"
                            data-testid={`${DEFAULT_TEST_ID}__trustpilot-widget`}
                            theme={TrustpilotTheme.DARK}
                        />
                    </div>
                </div>
                <div
                    className="product-trust-block__usp-blocks"
                    data-testid={`${DEFAULT_TEST_ID}__usp-blocks`}
                >
                    {uspBlocks}
                </div>
            </div>
        </StyledTrustBlock>
    );
};
